import { useRef, useState } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { FileSpreadsheet, Upload, Download, ArrowLeftRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RotaAllocationSection = ({ eventName }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [swapCtx, setSwapCtx] = useState(null);
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState('');

  const openSwap = async (ctx) => {
    setSwapCtx(ctx);
    setSearch('');
    try {
      const r = await axios.get(`${API}/admin/events/rota/staff`, { params: { location: eventName } });
      setStaff(r.data);
    } catch {
      setStaff((result?.staff_summary || []).map((s) => ({ user_id: s.employee_id, user_name: s.employee_name })));
    }
  };

  const doSwap = async (b) => {
    try {
      const payload = swapCtx.mode === 'single'
        ? { mode: 'single', row: swapCtx.row, employee_b: b.user_id, employee_b_name: b.user_name }
        : { mode: 'all', employee_a: swapCtx.employeeA.id, employee_b: b.user_id, employee_b_name: b.user_name };
      const r = await axios.post(`${API}/admin/events/rota/swap/${result.file_id}`, payload);
      setResult(r.data);
      setSwapCtx(null);
      toast.success(swapCtx.mode === 'single' ? 'Shift swapped' : 'All schedules swapped');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Swap failed');
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('location', eventName);
      fd.append('file', file);
      const r = await axios.post(`${API}/admin/events/rota/upload`, fd);
      setResult(r.data);
      toast.success(`Allocated ${r.data.allocated}/${r.data.total_rows} shifts`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rota allocation failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const approve = async () => {
    try {
      const r = await axios.post(`${API}/admin/events/rota/approve/${result.file_id}`);
      setResult({ ...result, status: 'approved' });
      toast.success(`Published: ${r.data.shifts_created + r.data.shifts_updated} shifts in employee portals`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Approve failed');
    }
  };

  const download = async () => {
    try {
      const r = await axios.get(`${API}/admin/events/rota/download/${result.file_id}`, { responseType: 'blob' });
      saveAs(new Blob([r.data]), `${eventName.replace(/[^a-zA-Z0-9 _-]/g, '')}_COMPLETED.xlsx`);
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <Card className="bg-card border-border rounded-sm" data-testid="rota-allocation-section">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-medium flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-primary" />
            Rota Excel Allocation
          </p>
          <div className="flex gap-2">
            <input ref={inputRef} type="file" accept=".xlsx" className="hidden"
              onChange={(e) => upload(e.target.files?.[0])} data-testid="rota-file-input" />
            <Button size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()}
              className="rounded-sm text-xs uppercase" data-testid="rota-upload-btn">
              <Upload className="w-3 h-3 mr-1" />
              {busy ? 'Allocating…' : 'Upload Rota Excel'}
            </Button>
            {result && result.status !== 'approved' && (
              <Button size="sm" onClick={approve}
                className="rounded-sm text-xs uppercase bg-emerald-600 text-white hover:bg-emerald-500"
                data-testid="rota-approve-btn">
                Approve & Publish
              </Button>
            )}
            {result && result.status === 'approved' && (
              <Button size="sm" onClick={download}
                className="rounded-sm text-xs uppercase bg-primary text-primary-foreground hover:bg-yellow-300"
                data-testid="rota-download-btn">
                <Download className="w-3 h-3 mr-1" />
                Download Completed Excel
              </Button>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Upload the blank rota — staff from this event card are allocated fairly. Re-upload a completed
          file with some names cleared: named rows stay locked, only the blanks get auto-allocated.
        </p>
        <Dialog open={!!swapCtx} onOpenChange={(o) => !o && setSwapCtx(null)}>
          <DialogContent className="max-w-sm" data-testid="rota-swap-dialog">
            <DialogHeader>
              <DialogTitle className="text-sm">
                {swapCtx?.mode === 'single'
                  ? `Swap shift ${swapCtx?.shiftLabel} — ${swapCtx?.employeeA?.name} with:`
                  : `Swap ALL shifts of ${swapCtx?.employeeA?.name} with:`}
              </DialogTitle>
            </DialogHeader>
            <Input autoFocus placeholder="Search employee…" value={search}
              onChange={(e) => setSearch(e.target.value)} className="h-8 text-sm" data-testid="swap-search-input" />
            <div className="max-h-60 overflow-y-auto space-y-1" data-testid="swap-staff-list">
              {staff
                .filter((s) => s.user_id !== swapCtx?.employeeA?.id
                  && s.user_name.toLowerCase().includes(search.toLowerCase()))
                .map((s) => (
                  <button key={s.user_id} onClick={() => doSwap(s)}
                    className="w-full text-left px-3 py-2 text-sm rounded-sm border border-border hover:bg-accent hover:text-accent-foreground transition-colors"
                    data-testid={`swap-pick-${s.user_id}`}>
                    {s.user_name}
                  </button>
                ))}
              {staff.filter((s) => s.user_id !== swapCtx?.employeeA?.id
                && s.user_name.toLowerCase().includes(search.toLowerCase())).length === 0 && (
                <p className="text-xs text-muted-foreground px-1 py-2">No employees found</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
        {result && (
          <div className="space-y-2 text-xs" data-testid="rota-summary">
            <p className="text-foreground font-medium">
              {result.status === 'approved' ? '✅ Approved & published. ' : '⏳ DRAFT — review below, then Approve & Publish. '}
              Allocated {result.allocated}/{result.total_rows} shifts
              {(result.locked_rows || 0) > 0 && ` · ${result.locked_rows} kept from file`}
              {result.unallocated.length > 0 && ` · ${result.unallocated.length} UNALLOCATED`}
            </p>
            {(result.unmatched_names || []).length > 0 && (
              <p className="text-amber-500" data-testid="rota-unmatched-names">
                Names not in event staff (auto-reallocated): {result.unmatched_names.map((u) => `"${u.name}" (row #${u.row}, ${u.date})`).join(', ')}
              </p>
            )}
            {result.day_breakdown && (
              <div className="flex flex-wrap gap-2">
                {result.day_breakdown.map((d) => (
                  <span key={d.date} className={`px-2 py-0.5 rounded-sm border ${d.unallocated > 0 ? 'border-red-500/40 text-red-400' : 'border-border text-muted-foreground'}`}>
                    {d.date}: {d.allocated}/{d.rows_in_excel} rows · {d.staff_available} staff
                  </span>
                ))}
              </div>
            )}
            {result.capacity_updates.length > 0 && (
              <p className="text-amber-500">
                Capacity raised: {result.capacity_updates.map((c) => `${c.date} ${c.from}→${c.to}`).join(', ')}
              </p>
            )}
            <div className="space-y-1 max-h-64 overflow-y-auto border border-border rounded-sm p-2" data-testid="rota-per-employee">
              {result.staff_summary.map((s) => (
                <details key={s.employee_id}>
                  <summary className="cursor-pointer text-muted-foreground">
                    {s.employee_name}: <strong className="text-foreground">{s.shifts} shifts · {s.hours}h</strong>
                    {result.status !== 'approved' && (
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openSwap({ mode: 'all', employeeA: { id: s.employee_id, name: s.employee_name } }); }}
                        className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase border border-border rounded-sm text-primary hover:bg-accent transition-colors"
                        data-testid={`swap-all-btn-${s.employee_id}`}>
                        <ArrowLeftRight className="w-3 h-3" /> Swap All
                      </button>
                    )}
                  </summary>
                  <div className="pl-4 py-1 space-y-0.5">
                    {(result.allocations || []).filter((a) => a.employee_id === s.employee_id).map((a, i) => (
                      <p key={i} className="font-mono text-muted-foreground flex items-center gap-2">
                        <span>{a.date} · {a.start}–{a.end} · {a.position || '—'}</span>
                        {a.locked && (
                          <span className="px-1 py-0.5 text-[9px] uppercase border border-emerald-500/40 text-emerald-500 rounded-sm" data-testid={`locked-badge-${a.row}`}>kept</span>
                        )}
                        {result.status !== 'approved' && (
                          <button
                            onClick={() => openSwap({ mode: 'single', row: a.row, shiftLabel: `${a.date} ${a.start}–${a.end}`, employeeA: { id: s.employee_id, name: s.employee_name } })}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase border border-border rounded-sm text-primary hover:bg-accent transition-colors"
                            data-testid={`swap-shift-btn-${a.row}`}>
                            <ArrowLeftRight className="w-3 h-3" /> Swap
                          </button>
                        )}
                      </p>
                    ))}
                  </div>
                </details>
              ))}
            </div>
            {result.unallocated.length > 0 && (
              <div className="text-red-400">
                Unallocated rows: {result.unallocated.map((u) => `#${u.row} (${u.date} ${u.time})`).join(', ')}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RotaAllocationSection;
